const LOCALE = 'es-EC';

const DATE_ONLY = /^\d{4}-\d{2}-\d{2}$/;

/**
 * Formats a **calendar date** (`YYYY-MM-DD`) as `DD/MM/YYYY`.
 *
 * Deliberately does not go through `new Date()`. A date-only string parses as
 * UTC midnight, so rendering it in `America/Guayaquil` (UTC−5) shifts it to the
 * previous day — a birth date entered as the 1st would display as the 31st, and
 * nobody would suspect the formatter. A calendar date has no instant and no
 * timezone; it is three numbers, and it is rendered as three numbers.
 */
export const formatDate = (value: string | null | undefined): string => {
  if (!value) return '—';
  if (DATE_ONLY.test(value)) {
    const [year, month, day] = value.split('-');
    return `${day}/${month}/${year}`;
  }
  return formatDateTime(value);
};

/**
 * Formats an **instant** (an ISO timestamp) in the business's timezone.
 *
 * The timezone belongs to the business, not to the browser: a salon in
 * Guayaquil reads its own day boundaries, whether the device is in Quito or on
 * holiday in Madrid. Callers pass `business.timezone`; omitting it falls back to
 * the device, which is only right before the business settings have loaded.
 */
export const formatDateTime = (value: string | null | undefined, timeZone?: string): string => {
  if (!value) return '—';
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return '—';
  // Explicit parts rather than `dateStyle: 'short'`, which renders a two-digit
  // year in this locale — ambiguous in a record people will still be reading in
  // five years. 24h for the same reason: an appointment at "8:30" is a support
  // call waiting to happen.
  return new Intl.DateTimeFormat(LOCALE, {
    timeZone,
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  }).format(parsed);
};

/** Just the clock time of an instant, in the business's timezone: `14:30`. */
export const formatTime = (value: string | null | undefined, timeZone?: string): string => {
  if (!value) return '—';
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return '—';
  return new Intl.DateTimeFormat(LOCALE, {
    timeZone,
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  }).format(parsed);
};

const PART_FORMAT: Intl.DateTimeFormatOptions = {
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
  hour: '2-digit',
  minute: '2-digit',
  second: '2-digit',
  hour12: false,
};

interface ZonedParts {
  year: number;
  month: number;
  day: number;
  hour: number;
  minute: number;
  second: number;
}

/**
 * What an instant reads as on a clock in `timeZone`.
 *
 * `Intl` is the only thing in the browser that knows a zone's offset on a given
 * date, so every conversion in this file goes through it rather than through an
 * offset somebody typed in — which is right until the day it isn't, and then is
 * silently an hour out for half the year.
 */
const zonedParts = (date: Date, timeZone?: string): ZonedParts => {
  const parts = new Intl.DateTimeFormat('en-US', { ...PART_FORMAT, timeZone }).formatToParts(date);
  const read = (type: Intl.DateTimeFormatPartTypes): number =>
    Number(parts.find((part) => part.type === type)?.value ?? '0');
  return {
    year: read('year'),
    month: read('month'),
    day: read('day'),
    // `hour12: false` renders midnight as 24 in some engines.
    hour: read('hour') % 24,
    minute: read('minute'),
    second: read('second'),
  };
};

/**
 * The calendar date an instant falls on, in the business's timezone.
 *
 * The calendar is laid out by local day: an appointment at 19:00 in Guayaquil
 * is 00:00 UTC the next morning, and grouping it by the UTC date would draw it
 * on tomorrow's column.
 */
export const zonedDateKey = (value: string, timeZone?: string): string => {
  const { year, month, day } = zonedParts(new Date(value), timeZone);
  return `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
};

/** Minutes since local midnight — where an appointment sits on the time grid. */
export const zonedMinutes = (value: string, timeZone?: string): number => {
  const { hour, minute } = zonedParts(new Date(value), timeZone);
  return hour * 60 + minute;
};

/** How far apart two instants are, in minutes. */
export const minutesBetween = (from: string, to: string): number =>
  Math.round((new Date(to).getTime() - new Date(from).getTime()) / 60000);

/**
 * A local date and clock time in `timeZone`, as the instant it names.
 *
 * Two passes, because the offset itself depends on the answer: the first guess
 * is corrected using the zone's offset *at that guess*, which resolves every
 * date except the ambiguous hour of a backwards DST shift — where either answer
 * is defensible and this one picks the later offset.
 */
export const zonedToInstant = (dateKey: string, time: string, timeZone?: string): string => {
  const naive = Date.parse(`${dateKey}T${time.slice(0, 5)}:00Z`);
  if (Number.isNaN(naive)) return '';

  const offsetAt = (moment: number): number => {
    const { year, month, day, hour, minute, second } = zonedParts(new Date(moment), timeZone);
    return Date.UTC(year, month - 1, day, hour, minute, second) - moment;
  };

  const first = naive - offsetAt(naive);
  return new Date(naive - offsetAt(first)).toISOString();
};

/** Today, as a `YYYY-MM-DD` key in the business's timezone. */
export const todayKey = (timeZone?: string): string =>
  zonedDateKey(new Date().toISOString(), timeZone);

/** The current clock time, as `HH:MM` in the business's timezone. */
export const currentTimeKey = (timeZone?: string, now: Date = new Date()): string => {
  const minutes = zonedMinutes(now.toISOString(), timeZone);
  return `${String(Math.floor(minutes / 60)).padStart(2, '0')}:${String(minutes % 60).padStart(2, '0')}`;
};

/** Shifts a `YYYY-MM-DD` key by whole days, without ever touching a timezone. */
export const addDays = (dateKey: string, days: number): string => {
  const [year, month, day] = dateKey.split('-').map(Number);
  // Anchored at UTC noon so a whole-day step can never land on a DST boundary
  // and come back as the previous day.
  const moved = new Date(Date.UTC(year, month - 1, day, 12));
  moved.setUTCDate(moved.getUTCDate() + days);
  return moved.toISOString().slice(0, 10);
};

/**
 * Renders a `YYYY-MM-DD` key without ever letting it through a timezone.
 *
 * A calendar key names three numbers, not an instant: parsing it as UTC
 * midnight and formatting it in `America/Guayaquil` would print the day before.
 * Anchoring at UTC and formatting in UTC keeps the three numbers intact — the
 * same reason `formatDate` exists.
 */
export const formatDateKey = (dateKey: string, options: Intl.DateTimeFormatOptions): string => {
  const [year, month, day] = dateKey.split('-').map(Number);
  return new Intl.DateTimeFormat(LOCALE, { ...options, timeZone: 'UTC' }).format(
    new Date(Date.UTC(year, month - 1, day))
  );
};

/** "dom, 27 sept" — a day short enough for a chip or a toast. */
export const formatDayShort = (dateKey: string): string =>
  formatDateKey(dateKey, { weekday: 'short', day: 'numeric', month: 'short' });

/** "27 sept 2026" — a past date in a history, where the year matters. */
export const formatDateShort = (dateKey: string): string =>
  formatDateKey(dateKey, { day: 'numeric', month: 'short', year: 'numeric' });

/** Whole days from one `YYYY-MM-DD` key to another; negative when `to` is earlier. */
export const daysBetween = (from: string, to: string): number => {
  const utc = (key: string) => {
    const [year, month, day] = key.split('-').map(Number);
    return Date.UTC(year, month - 1, day);
  };
  return Math.round((utc(to) - utc(from)) / 86_400_000);
};

/** 0 = Monday .. 6 = Sunday, matching the API's `weekday`. */
export const weekdayOf = (dateKey: string): number => {
  const [year, month, day] = dateKey.split('-').map(Number);
  return (new Date(Date.UTC(year, month - 1, day)).getUTCDay() + 6) % 7;
};
